import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('adminDashboard')}</h1>
              <p className="text-gray-600">{t('welcome')}, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="btn-secondary"
            >
              {t('signOut')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending Approvals */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('pendingApprovals')}
              </h2>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium">{t('noPendingApprovals')}</p>
                  <p className="text-yellow-600 text-sm mt-1">
                    {t('allUsersProcessed')}
                  </p>
                </div>
                <button className="btn-primary w-full">
                  {t('viewAllUsers')}
                </button>
              </div>
            </div>

            {/* System Statistics */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('systemStatistics')}
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">0</p>
                    <p className="text-sm text-gray-600">{t('totalUsers')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">0</p>
                    <p className="text-sm text-gray-600">{t('activeJobs')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">0</p>
                    <p className="text-sm text-gray-600">{t('checkInsToday')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">0</p>
                    <p className="text-sm text-gray-600">{t('pendingIssues')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('quickActions')}
              </h2>
              <div className="space-y-3">
                <button className="btn-primary w-full">
                  {t('createNewJob')}
                </button>
                <button className="btn-secondary w-full">
                  {t('manageUsers')}
                </button>
                <button className="btn-secondary w-full">
                  {t('viewReports')}
                </button>
                <button className="btn-secondary w-full">
                  {t('systemSettings')}
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card lg:col-span-2">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('recentSystemActivity')}
              </h2>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">{t('noRecentActivity')}</p>
                </div>
              </div>
            </div>

            {/* Admin Info */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('adminInformation')}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('name')}</label>
                  <p className="text-gray-900">{user?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('email')}</label>
                  <p className="text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('role')}</label>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    {t('administrator')}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('status')}</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user?.status === 'approved' ? t('approved') : t('pending')}
                  </span>
                </div>
              </div>
            </div>

            {/* System Health */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('systemHealth')}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('apiStatus')}:</span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {t('online')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('database')}:</span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {t('connected')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t('authentication')}:</span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {t('active')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard; 